import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ExpenseCreate,
  ExpenseList,
  ExpenseDelete,
  ExpenseUpdate,
  ApproveExpense,
} from "../../../Services/Services";

const ExpenseManagementTable = () => {
  const [expenses, setExpenses] = useState([]);
  const [filter, setFilter] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newExpenseName, setNewExpenseName] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editExpenseName, setEditExpenseName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [errors, setErrors] = useState({});

  // ✅ NEW - Approve/Reject Modal States
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [expenseToApprove, setExpenseToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null); // "Approved" | "Rejected"

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedExpense, setExpandedExpense] = useState(null);

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    refreshExpenses();
  }, []);

  const refreshExpenses = async () => {
    try {
      const res = await ExpenseList();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.results)
          ? res.results
          : [];
      setExpenses(list);
    } catch (error) {
      toast.error("Unable to load expenses");
    }
  };

  // ✅ UPDATED - Approve/Reject handler
  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveExpense(
        expenseToApprove.expense_id,
        approvedById,
        actionType,
        remarkInput
      );

      setExpenses((prev) =>
        prev.map((item) =>
          item.expense_id === expenseToApprove.expense_id ? updated : item
        )
      );

      const msg =
        actionType === "Approved"
          ? "Expense approved successfully!"
          : "Expense rejected successfully!";
      toast.success(msg);

      // ✅ Close modals and reset
      setOpenApproveRejectModal(false);
      setOpenEditModal(false);
      setRemarkInput("");
      setActionType(null);
      setExpenseToApprove(null);
    } catch (error) {
      toast.error("Failed to update expense status.");
    }
  };

  const handleEdit = (expense) => {
    const editRow = expenses.find((d) => d.expense_id === expense.expense_id);
    if (editRow) {
      setEditExpenseName(editRow.expense);
      setEditingIndex(editRow);
      setExpenseToApprove(null); // ✅ Clear approval state for normal edit
      setOpenEditModal(true);
    }
  };

  const handleUpdateExpense = async () => {
    const trimmedName = editExpenseName.trim();
    if (trimmedName === "") {
      setErrors({ expense: "This field is required." });
      return;
    }

    const exists = expenses.some(
      (e) =>
        e.expense_id !== editingIndex.expense_id &&
        e.expense?.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (exists) {
      toast.info("Expense already exists");
      setErrors({ expense: "Expense already exists" });
      return;
    }

    setErrors({});
    try {
      await ExpenseUpdate(editingIndex.expense_id, { expense: trimmedName });
      toast.success("Expense updated successfully!");
      setOpenEditModal(false);
      setEditingIndex(null);
      setEditExpenseName("");
      setExpenseToApprove(null);
      await refreshExpenses();
    } catch (error) {
      toast.error("Failed to update expense");
    }
  };

  const handleAddNewExpense = async () => {
    const trimmedExpense = newExpenseName.trim();
    if (trimmedExpense === "") {
      setErrors({ expense: "Expense name is required" });
      return;
    }

    const exists = expenses.some(
      (e) => e.expense?.trim().toLowerCase() === trimmedExpense.toLowerCase()
    );
    if (exists) {
      toast.info("Expense already exists");
      setErrors({ expense: "Expense already exists" });
      return;
    }

    setErrors({});
    try {
      await ExpenseCreate({ expense: trimmedExpense });
      toast.success("Expense added successfully!");
      setNewExpenseName("");
      setOpenAddModal(false);
      await refreshExpenses();
    } catch (error) {
      toast.error("Failed to add expense");
    }
  };

  const handleDelete = async (expense_id) => {
    try {
      await ExpenseDelete(expense_id);
      toast.info("Expense deleted.");
      await refreshExpenses();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const getFilteredSortedExpenses = () => {
    return expenses.filter((expense) =>
      expense?.expense?.toLowerCase().includes(filter.toLowerCase())
    );
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ✅ Mobile Card Component
  const MobileExpenseCard = ({ expense, index }) => {
    const isExpanded = expandedExpense === expense.expense_id;

    return (
      <Box key={expense.expense_id}>
        <Box
          onClick={() =>
            setExpandedExpense(
              expandedExpense === expense.expense_id ? null : expense.expense_id
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${expense.status === "Approved"
              ? "#4caf50"
              : expense.status === "Rejected"
                ? "#f44336"
                : "#ff9800"
              }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {expense.expense}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Chip
                label={expense.status || "Pending"}
                color={
                  expense.status === "Approved"
                    ? "success"
                    : expense.status === "Rejected"
                      ? "error"
                      : "warning"
                }
                size="small"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {formatOnlyDate(expense.created_at)}
              </span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${expense.status === "Approved"
                ? "#4caf50"
                : expense.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
                }`,
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={expense.status || "Pending"}
                  color={
                    expense.status === "Approved"
                      ? "success"
                      : expense.status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  DATE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {formatOnlyDate(expense.created_at)}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  APPROVED BY
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {expense.authorized_by_name || "—"}
                </Typography>
              </Box>
            </Box>

            {expense.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>
                  {expense.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* ✅ Checkbox - Opens edit modal in approval mode */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      expense.status === "Approved"
                        ? "#e8f5e9"
                        : expense.status === "Rejected"
                          ? "#ffebee"
                          : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${expense.status === "Approved"
                      ? "#4caf50"
                      : expense.status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                      }`,
                  }}
                >
                  <Checkbox
                    checked={
                      expense.status === "Approved" || expense.status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setEditExpenseName(expense.expense);
                      setEditingIndex(expense);
                      setExpenseToApprove(expense);
                      setOpenEditModal(true);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.75)",
                      color:
                        expense.status === "Approved"
                          ? "#4caf50"
                          : expense.status === "Rejected"
                            ? "#f44336"
                            : "#ff9800",
                      "&.Mui-checked": {
                        color: expense.status === "Approved" ? "#4caf50" : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        expense.status === "Approved"
                          ? "#2e7d32"
                          : expense.status === "Rejected"
                            ? "#c62828"
                            : "#f57c00",
                    }}
                  >
                    {expense.status === "Approved"
                      ? "Approved"
                      : expense.status === "Rejected"
                        ? "Rejected"
                        : "Approve"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit - Normal mode */}
                <IconButton
                  size="small"
                  disabled={expense.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(expense);
                  }}
                  sx={{
                    bgcolor: expense.status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: expense.status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform: expense.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color: expense.status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={expense.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setExpenseToDelete(expense.expense_id);
                  }}
                  sx={{
                    bgcolor: expense.status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: expense.status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform: expense.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1rem",
                      color: expense.status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: -3,
          pt: -2,
          mb: 1,
        }}
      >
        <TextField
          label="Search by Expense"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
          sx={{
            mr: 2,
            "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddModal(true)}
          sx={{
            height: 30,
            minWidth: 120,
            fontSize: "0.7rem",
            borderRadius: 0,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Expense
        </Button>
      </Box>

      <Box
        id="project-table-print"
        sx={{
          maxHeight: isMobile ? "70vh" : "88vh",
          overflowY: "auto",
        }}
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {getFilteredSortedExpenses().map((expense, index) => (
              <MobileExpenseCard key={expense.expense_id} expense={expense} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "88vh",
              width: "100%",
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: { xs: 600, sm: 800, md: 1000, lg: 1200 },
                tableLayout: "auto",
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem !important",
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#2196f3" : "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                    },
                  }}
                >
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                        zIndex: 999,
                        minWidth: 100,
                        paddingLeft: "30px",
                      }}
                    >
                      Approve
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 100 : 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      zIndex: 999,
                      minWidth: 70,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Expense</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      zIndex: 999,
                      minWidth: 150,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredSortedExpenses().map((expense, index) => (
                  <TableRow
                    hover
                    key={expense.expense_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": { backgroundColor: "#e3f2fd !important" },
                      },
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                    }}
                  >
                    {/* ✅ Checkbox */}
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "black"
                              : index % 2 === 0
                                ? "#fafafa"
                                : "#ffffff",
                          zIndex: 998,
                          minWidth: 100,
                          "&:hover": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        }}
                      >
                        <Checkbox
                          checked={
                            expense.status === "Approved" ||
                            expense.status === "Rejected"
                          }
                          onChange={() => {
                            setEditExpenseName(expense.expense);
                            setEditingIndex(expense);
                            setExpenseToApprove(expense);
                            setOpenEditModal(true);
                          }}
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                            transform: "scale(0.6)",
                            color:
                              expense.status === "Approved"
                                ? "green"
                                : expense.status === "Rejected"
                                  ? "red"
                                  : "grey",
                            "&.Mui-checked": {
                              color:
                                expense.status === "Approved" ? "green" : "red",
                            },
                          }}
                        />
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 100 : 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : index % 2 === 0
                              ? "#fafafa"
                              : "#ffffff",
                        zIndex: 998,
                        minWidth: 70,
                        "&:hover": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell>{expense.expense}</TableCell>

                    <TableCell>
                      <Chip
                        label={expense.status || "Pending"}
                        color={
                          expense.status === "Approved"
                            ? "success"
                            : expense.status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>{expense.authorized_by_name || "—"}</TableCell>
                    <TableCell>{formatOnlyDate(expense.created_at)}</TableCell>
                    <TableCell>{expense.remarks || "—"}</TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "black"
                            : index % 2 === 0
                              ? "#fafafa"
                              : "#ffffff",
                        zIndex: 998,
                        minWidth: 150,
                        "&:hover": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      }}
                    >
                      {/* ✅ Edit */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleEdit(expense)}
                        disabled={expense.status === "Approved"}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>

                      {/* Delete */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setExpenseToDelete(expense.expense_id);
                        }}
                        disabled={expense.status === "Approved"}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ✅ UPDATED - Edit Modal with Conditional Footer */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setExpenseToApprove(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogContent>
          <TextField
            label="Expense Name"
            value={editExpenseName}
            onChange={(e) => {
              setEditExpenseName(e.target.value);
              if (e.target.value.trim() !== "") setErrors({});
            }}
            fullWidth
            margin="normal"
            required
            error={Boolean(errors.expense)}
            helperText={errors.expense}
          />
        </DialogContent>

        <DialogActions>
          {/* ✅ Show Approve/Reject ONLY when checkbox was clicked */}
          {expenseToApprove && userRole != "Staff" && (
            <>
              <Button
                color="success"
                size="small"
                onClick={() => {
                  setActionType("Approved");
                  setOpenApproveRejectModal(true);
                }}
              >
                Approve
              </Button>

              <Button
                sx={{color:'orange'}}
                size="small"
                onClick={() => {
                  setActionType("Rejected");
                  setOpenApproveRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={handleUpdateExpense} color="primary">
            Edit
          </Button>

          <Button
            onClick={() => {
              setOpenEditModal(false);
              setExpenseToApprove(null);
            }}
            color="error"
          >
            Close
          </Button>

        </DialogActions>
      </Dialog>

      {/* Add Modal */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Expense</DialogTitle>
        <DialogContent>
          <TextField
            label="Expense Name"
            value={newExpenseName}
            onChange={(e) => {
              setNewExpenseName(e.target.value);
              if (e.target.value.trim() !== "") setErrors({});
            }}
            fullWidth
            margin="normal"
            required
            error={Boolean(errors.expense)}
            helperText={errors.expense}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
          <Button onClick={handleAddNewExpense} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW - Approve/Reject Modal */}
      <Dialog
        open={openApproveRejectModal}
        onClose={() => {
          setOpenApproveRejectModal(false);
          setRemarkInput("");
          setActionType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionType === "Approved" ? "Approve Expense" : "Reject Expense"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter a remark before{" "}
            {actionType === "Approved" ? "approving" : "rejecting"} this expense:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            placeholder="Enter remark..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApproveRejectModal(false);
              setRemarkInput("");
              setActionType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color={actionType === "Approved" ? "success" : "error"}
            variant="contained"
            onClick={handleApproveReject}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setExpenseToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(expenseToDelete);
                setAnchorEl(null);
                setExpenseToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setExpenseToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default ExpenseManagementTable;